/**
 * Light/dark theme handling shared by the operator + portal surfaces
 * (docs/architecture.md §6.9).
 *
 * The server is the source of truth: it renders `<html data-theme>` from the
 * `theme` cookie (defaulting to **light**) so the first paint is correct with no
 * flash. The client trusts that rendered value rather than re-deriving from the
 * OS preference — otherwise a dark-mode OS would override an explicit light
 * choice on every load. `localStorage` (key `g4-theme`, matching the mockups) is
 * kept as a fallback for contexts without a server-rendered attribute.
 */
const STORAGE_KEY = 'g4-theme'
export type Theme = 'light' | 'dark'

function isTheme(value: string | null): value is Theme {
  return value === 'light' || value === 'dark'
}

export function getTheme(): Theme {
  // 1) The server-rendered attribute (derived from the cookie) is authoritative.
  if (typeof document !== 'undefined') {
    const rendered = document.documentElement.getAttribute('data-theme')
    if (isTheme(rendered)) return rendered
  }
  // 2) Fallback: an explicit client choice persisted in localStorage.
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (isTheme(stored)) return stored
  }
  // 3) Default to light (no OS-preference auto-dark).
  return 'light'
}

export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme)
}

export function setTheme(theme: Theme): void {
  if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, theme)
  applyTheme(theme)
}

export function toggleTheme(): Theme {
  const next: Theme = getTheme() === 'dark' ? 'light' : 'dark'
  setTheme(next)
  return next
}

// Apply as early as possible to avoid a flash of the wrong theme.
export function initTheme(): void {
  applyTheme(getTheme())
}
